import React, { useState, useMemo, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, Outlet } from 'react-router-dom';

// --- IMPORTS DE ESTILO ---
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles.css';

// --- IMPORTS DE BIBLIOTECAS ---
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons'; // 1. Importar a biblioteca de ícones regulares
import { Spinner } from 'react-bootstrap';

// --- IMPORTS DE PÁGINAS E COMPONENTES ---
import Financeiro from './pages/Financeiro';
import Navbar from './components/Navbar';
import Consultorio from './pages/Consultorio';
import Estoque from './pages/Estoque';
import PessoalPage from './pages/Funcionario'; // Renomear importação para PessoalPage
import Servicos from './pages/Servicos';
import Login from './pages/Login'; // Importar o componente de Login - CORRIGIDO
import DashboardPage from './pages/DashboardPage'; // Importar DashboardPage
import PerfilUsuarioPage from './pages/PerfilUsuarioPage'; // Importar a nova página de Perfil
import PerfisPage from './pages/PerfisPage'; // Importar a nova página de Perfis
import RelatoriosPage from './pages/RelatoriosPage'; // Importar a nova página de Relatórios
import ComunicadosPage from './pages/ComunicadosPage'; // Importar a nova página de Comunicados
import ConfiguracoesPage from './pages/ConfiguracoesPage'; // Importar a nova página de Configurações
import RegisterPage from './pages/onboarding/RegisterPage'; // Importe a nova página de registro
import PricingPage from './components/PricingPage'; // Importar a página de planos
import LandingPage from './pages/LandingPage'; // Importar a Landing Page
import PublicServicosPage from './pages/social/PublicServicosPage'; // Importar a página pública de serviços
import BuyCreditsPage from './pages/BuyCreditsPage';
import PainelChamadas from './pages/PainelChamadas'; // Importar a nova página do Painel
import AuditoriaPage from './pages/AuditoriaPage';
import MarketingPage from './pages/MarketingPage'; // Importar a nova página de Marketing
import LeadCapturePage from './pages/public/LeadCapturePage'; // Importar a página de captura de lead

// --- IMPORTS DE LAYOUTS ---
import MainLayout from './layouts/MainLayout'; // Layout para páginas protegidas
import PublicLayout from './layouts/PublicLayout'; // Layout para páginas públicas como Login
import OnboardingLayout from './layouts/OnboardingLayout';
import OnboardingCollaboratorsPage from './pages/onboarding/OnboardingCollaboratorsPage';
import OnboardingMaterialsPage from './pages/onboarding/OnboardingMaterialsPage'; // Crie este arquivo
import OnboardingServicesPage from './pages/onboarding/OnboardingServicesPage';   // Crie este arquivo

// --- IMPORTS DO PAINEL ADMIN ---
import AdminLayout from './admin/layouts/AdminLayout';
import AdminLoginPage from './admin/pages/AdminLoginPage'; // Você precisará criar este arquivo
import AdminDashboardPage from './admin/pages/AdminDashboardPage';
import AdminEntidadesPage from './admin/pages/AdminEntidadesPage';
import AdminUsersPage from './admin/pages/AdminUsersPage';
import AdminSubscriptionsPage from './admin/pages/AdminSubscriptionsPage';
import ImpersonationBanner from './components/ImpersonationBanner'; // Importar o banner
import SubscriptionBanner from './components/SubscriptionBanner'; // Importar o novo banner de assinatura

// --- IMPORTS DE CONTEXTO E COMPONENTES GLOBAIS ---
import { AppointmentsProvider } from './AppointmentsContext';
import { AlertModalProvider } from './AlertModalContext'; // Importar o novo provider
import { useAuthContext } from './context/AuthContext'; // Usar o novo AuthContext - CORRIGIDO
import { useEntidadeContext } from './EntidadeContext';
import { usePacienteContext } from './PacienteContext';
import { useUserContext } from './UserContext';
import AlertModalComponent from './components/AlertModalComponent'; // Importar o componente do modal

// --- CONFIGURAÇÃO DE BIBLIOTECAS (EXECUTÁVEL) ---
library.add(fas, far); // 2. Adiciona todos os ícones sólidos E regulares à biblioteca

function App() {
  const [activeHeaderTabKey, setActiveHeaderTabKey] = useState('consultorio'); // Estado para controlar a aba ativa
   // NOVO: Estado para controlar os toasts

  const { authState, isLoggedIn, loggedInUser, loginDateTime, handleLogout, currentEntidadeAuth } = useAuthContext(); // Pegar currentEntidadeAuth
  const { currentEntidade, setCurrentEntidade: setEntidadeInContext, isLoadingEntidade } = useEntidadeContext(); // Renomeado para evitar conflito
  const { currentUserRole, setCurrentUserRole, getPermittedHeaderTabs: getPermittedHeaderTabsFromContext, isLoadingAccessConfig } = useUserContext(); // Adicionado isLoadingAccessConfig
  const { pacienteSelecionado } = usePacienteContext(); // Obter paciente selecionado para exibir no Navbar
  const location = useLocation(); // Para obter a rota atual
  const navigate = useNavigate(); // Para navegação programática

  // Efeito para impedir a tradução automática do navegador, que causa tela em branco.
  useEffect(() => {
    const htmlElement = document.documentElement;
    htmlElement.setAttribute('translate', 'no');
    htmlElement.classList.add('notranslate');

    // Limpeza ao desmontar o componente
    return () => {
      htmlElement.removeAttribute('translate');
      htmlElement.classList.remove('notranslate');
    };
  }, []); // O array vazio garante que isso rode apenas uma vez

  // Função para obter as abas permitidas para o Navbar
  const permittedHeaderTabs = useMemo(() => {
    // A lógica de permissão de abas só deve ser aplicada para usuários do tipo 'clinica'
    if (!authState.roles?.includes('clinica') || !currentUserRole || !currentEntidade || isLoadingAccessConfig) {
      return [];
    }

    const allTabs = getPermittedHeaderTabsFromContext(currentEntidade, currentUserRole);

    // Reordena para garantir que o Dashboard apareça primeiro, se estiver presente
    const dashboardTab = allTabs.find(t => t.key === 'dashboard');
    if (dashboardTab) {
      return [dashboardTab, ...allTabs.filter(t => t.key !== 'dashboard')];
    }

    return allTabs;

  }, [authState.roles, currentUserRole, currentEntidade, getPermittedHeaderTabsFromContext, isLoadingAccessConfig]);

  // Mapeamento de roles que agora será dinâmico, usando useMemo.
  const roleMapping = useMemo(() => {
    const baseMapping = {
      'representante_legal': 'Representante Legal',
      'medico': 'Médico',
      'recepcao': 'Recepção',
    };

    // Se o usuário logado tiver um perfil customizado (indicado por entidade_perfil_id),
    // adiciona dinamicamente o mapeamento para esse perfil.
    if (loggedInUser?.entidade_perfil_id && loggedInUser?.role_key) {
      baseMapping[loggedInUser.role_key] = loggedInUser.profile_name;
    }

    return baseMapping;
  }, [loggedInUser]); // Recalcula o mapeamento sempre que o usuário logado mudar.

  // Sincroniza a role do AuthContext com o UserContext
  useEffect(() => {
    if (loggedInUser && loggedInUser.role_key) {
      const mappedRole = roleMapping[loggedInUser.role_key.toLowerCase()] || loggedInUser.role_key;
      console.log('baseMapping: ', roleMapping);
      console.log('mappedRole: ', mappedRole);
      setCurrentUserRole(mappedRole);
    } else if (!isLoggedIn) { // Se deslogado, limpa a role
      setCurrentUserRole(null);
    }
  }, [loggedInUser, isLoggedIn, setCurrentUserRole, roleMapping]); // Adiciona roleMapping como dependência

  // Sincroniza a entidade do AuthContext com o EntidadeContext
  useEffect(() => {
    if (isLoggedIn && currentEntidadeAuth) {
      setEntidadeInContext(currentEntidadeAuth);
    } else if (!isLoggedIn) { // Após logout
      setEntidadeInContext(null);
    }
  }, [isLoggedIn, currentEntidadeAuth, setEntidadeInContext]);


  // Ajusta activeHeaderTabKey se a atual não for permitida após login ou mudança de role/entidade
  // Também lida com o redirecionamento inicial após o login
  useEffect(() => {
    // Só executa se for um usuário de clínica, logado, onboarding completo, role e entidade definidos, E a configuração de acesso NÃO estiver carregando
    if (isLoggedIn && authState.roles?.includes('clinica') && authState.onboarding?.complete && currentUserRole && currentEntidade && !isLoadingAccessConfig) {
      if (permittedHeaderTabs.length > 0) {        
        const currentPathKey = location.pathname.split('/')[1]; // Pega a primeira parte do caminho, ex: "consultorio" ou "comunicados"
        const isValidCurrentPath = permittedHeaderTabs.some(tab => tab.key === currentPathKey);

        if (isValidCurrentPath) {
          if (activeHeaderTabKey !== currentPathKey) {
            setActiveHeaderTabKey(currentPathKey);
          }
        } else {
          // Se a rota atual não é uma aba de header (ex: /comunicados, /perfis),
          // apenas garantimos que nenhuma aba de header fique com a aparência de "ativa".
          setActiveHeaderTabKey('');
        }
      } else {
        // Se não há abas permitidas, o <ProtectedRoute> e a rota para /sem-acesso
        // já devem tratar o caso de um usuário logado sem permissões.
        // Apenas limpamos a aba ativa para que nenhum menu fique destacado.
        setActiveHeaderTabKey('');
      }
    }
    // Se alguma das condições no if principal não for atendida (ex: isLoadingAccessConfig é true),
    // este useEffect não fará nada, esperando a próxima renderização quando as dependências mudarem.
  }, [
    isLoggedIn,
    authState.roles,
    authState.onboarding?.complete, // Usar optional chaining aqui também para segurança
    currentUserRole,
    currentEntidade,
    isLoadingAccessConfig, // Nova dependência
    permittedHeaderTabs,
    location.pathname,
    navigate,
    activeHeaderTabKey // Manter activeHeaderTabKey para evitar loops se ele for definido e causar re-renderização
  ]);

  // Componente para rotas protegidas
  const ProtectedRoute = ({ children }) => {
    const location = useLocation(); // Para OnboardingGuard logic
    // OBTÉM OS DADOS DE ASSINATURA DO CONTEXTO
    const { authState, isFetchingSubscription } = useAuthContext();
  
    // 1. Não autenticado? Vai para o login.
    if (!isLoggedIn) {
      return <Navigate to="/login" replace />;
    }

    const isOnboardingRoute = location.pathname.startsWith('/onboarding');
  
    // 2. Onboarding pendente?
    if (authState.onboarding && !authState.onboarding.complete) {
      if (!isOnboardingRoute) {
        if (authState.onboarding.step === 'pending_materials') {
          return <Navigate to="/onboarding/materials" replace />;
        }
        if (authState.onboarding.step === 'pending_services') {
          return <Navigate to="/onboarding/services" replace />;
        }
        if (authState.onboarding.step === 'pending_collaborators') {
          return <Navigate to="/onboarding/collaborators" replace />;
        }
        return <Navigate to="/onboarding/materials" replace />;
      }
      return children;
    }
  
    // 3. Onboarding completo, mas usuário está em uma rota de onboarding?
    if (authState.onboarding && authState.onboarding.complete && isOnboardingRoute) {
      return <Navigate to={defaultProtectedRoute} replace />;
    }

    // --- NOVA LÓGICA DE ASSINATURA ---
    if (isFetchingSubscription) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Verificando assinatura...</span>
          </Spinner>
        </div>
      );
    }

    // 4. Para rotas principais (NÃO onboarding):
    // Mostrar spinner se os dados da entidade ou configuração de acesso estiverem carregando.
    if (isLoadingEntidade || isLoadingAccessConfig) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
        </div>
      );
    }
  
    // 5. Tudo certo, renderiza o conteúdo protegido.
    return children;
  };

  // Componente para rotas protegidas do Super Admin
  const AdminProtectedRoute = ({ children }) => {
    // A lógica agora verifica se o usuário está logado E se o array de roles do authState inclui 'super_admin'
    const isSuperAdmin = authState.roles?.includes('super_admin');
    if (!isLoggedIn || !isSuperAdmin) {
      return <Navigate to="/admin/login" replace />;
    }
    return children;
  };


  // Define a rota inicial padrão baseada nas abas permitidas ou um fallback
  const defaultProtectedRoute = useMemo(() => {
    // Regra 1: Se o perfil for 'representante_legal', o destino é sempre '/dashboard'.
    if (loggedInUser?.role_key === 'representante_legal') {
      return '/dashboard';
    }
    // Regra 2: Para todos os outros perfis, o destino padrão é '/consultorio'.
    // A lógica de permissão de rotas e abas cuidará de redirecionar para /sem-acesso
    // se o usuário não tiver acesso real a essa página.
    return '/dashboard';
  }, [loggedInUser]); // A dependência é apenas o usuário, que está disponível no login.

  return (
    <AlertModalProvider>
      <div className="app-container" style={{ backgroundColor: 'lavender'}}>
         <ImpersonationBanner />
        <SubscriptionBanner />
        <AlertModalComponent />
        <Routes>
          <Route element={<PublicLayout />}>
            {/* onLoginSuccess não é mais necessário aqui, pois o AuthContext lida com isso */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<RegisterPage />} /> {/* Rota pública para cadastro */}
            <Route path="/inicio" element={<LandingPage />} /> {/* Rota pública para a Landing Page */}
            <Route path="/clinica/:entidadeSlug" element={<PublicServicosPage />} /> {/* 2. Adicionar a nova rota pública */}

            {/* Nova rota pública para captura de leads por serviço */}
            <Route path="/lp/:entidadeSlug/:servicoSlug" element={<LeadCapturePage />} />

            {/* Rota pública para o painel de chamadas */}
            <Route path="/painel/:entidadeId" element={<PainelChamadas />} />
          </Route>

          {/* --- NOVAS ROTAS ADMINISTRATIVAS --- */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route 
            path="/admin" 
            element={
              <AdminProtectedRoute>
                <AdminLayout />
              </AdminProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboardPage />} />
            <Route path="entidades" element={<AdminEntidadesPage />} />
            <Route path="usuarios" element={<AdminUsersPage />} />
            <Route path="assinaturas" element={<AdminSubscriptionsPage />} />
          </Route>

          {/* Rotas de Onboarding Pós-Cadastro */}
          {/* A rota pai para /onboarding agora renderiza o layout, e as rotas filhas são aninhadas como JSX children */}
          <Route
            path="/onboarding" // Removido o /* aqui, pois as rotas filhas definirão os segmentos
            element={
              <ProtectedRoute>
                <OnboardingLayout /> {/* OnboardingLayout contém o Outlet */}
              </ProtectedRoute>
            }
          >
            {/* Estas são as rotas filhas que serão renderizadas pelo Outlet no OnboardingLayout */}
            <Route path="collaborators" element={<OnboardingCollaboratorsPage />} />
            <Route path="materials" element={<OnboardingMaterialsPage />} />
            <Route path="services" element={<OnboardingServicesPage />} />
            <Route index element={<Navigate to="collaborators" replace />} /> {/* Redireciona /onboarding (exato) para /onboarding/collaborators */}
          </Route>

          <Route
            path="/" // Rota raiz para o conteúdo autenticado principal
            element={
              <ProtectedRoute>
                <MainLayout
                  loginDateTime={loginDateTime}
                  activeHeaderTabKey={activeHeaderTabKey}
                  setActiveHeaderTabKey={setActiveHeaderTabKey}
                  onLogout={handleLogout}
                  userName={loggedInUser?.nome}
                  entidadeNome={currentEntidade?.nome}
                  pacienteNome={pacienteSelecionado?.nome}
                  permittedHeaderTabs={permittedHeaderTabs}
                >
                  {/* MainLayout agora usa <Outlet /> para renderizar as rotas filhas abaixo */}
                  {/* AppointmentsProvider envolverá o Outlet se todas as rotas filhas precisarem dele,
                      ou pode envolver rotas específicas abaixo. */}
                </MainLayout>
              </ProtectedRoute>
            }
          >
            {/* Rotas filhas que serão renderizadas pelo Outlet do MainLayout */}
            {/* Envolvemos um grupo de rotas com AppointmentsProvider usando uma rota de layout */}
            <Route element={<AppointmentsProvider><Outlet/></AppointmentsProvider>}>
              <Route path="consultorio" element={<Consultorio />} /> {/* Sua página de teste */}
              {/* <Route path="consultorio" element={<Consultorio />} />  // A rota original */}
              <Route path="financeiro" element={<Financeiro />} />
              {/* Adicione outras rotas que precisam do AppointmentsProvider aqui */}
            </Route>
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="estoque" element={<Estoque />} />
            <Route path="pessoal" element={<PessoalPage />} />
            <Route path="servicos" element={<Servicos />} />
            <Route path="perfis" element={<PerfisPage />} />
            <Route path="perfil" element={<PerfilUsuarioPage />} />
            <Route path="configuracoes" element={<ConfiguracoesPage />} />
            <Route path="relatorios" element={<RelatoriosPage />} />
            <Route path="comunicados" element={<ComunicadosPage />} />

            {/* Rota para a página de planos */}
            <Route path="marketing" element={<MarketingPage />} />


            <Route path="pricing" element={<PricingPage />} />
            <Route path="/comprar-creditos" element={<BuyCreditsPage />} />

            <Route path="/auditoria" element={
                <ProtectedRoute allowedRoles={['Representante Legal']}>
                    <AuditoriaPage />
                </ProtectedRoute>
            } />

            {/* Rota Index: Redireciona para a primeira aba permitida ou dashboard */}
            {/* CORREÇÃO: Renderiza o componente padrão diretamente em vez de navegar.
                Isso evita a condição de corrida onde a navegação cancela a exibição do modal de alerta.
                O `defaultProtectedRoute` ainda é usado para redirecionamentos em outros locais (ex: após onboarding).
            */}
            <Route index element={<DashboardPage />} />
            <Route path="sem-acesso" element={
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <h1>Sem Acesso</h1>
                <p>Você não tem permissão para acessar esta área ou nenhuma funcionalidade está configurada para seu perfil.</p>
              </div>
            } />
          </Route>

          {/* Rota catch-all para Not Found no final de todas as outras rotas */}
          <Route path="*" element={
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <h1>404 - Página Não Encontrada</h1>
            </div>
          } />
        </Routes>
      </div>
    </AlertModalProvider>
  );
}

export default App;
